/**
 * SPELL CHECK FOR THE COPY FIELDS (2026-08-22).
 *
 * A real Hunspell dictionary (en_US, via nspell) rather than a rule list, because
 * the whole point is catching `amenties` and `oppurtunity` — the typos that reach
 * 40,000 inboxes and that a rules-only checker cannot see.
 *
 * THREE THINGS THIS FILE EXISTS TO GET RIGHT.
 *
 * 1. THE DICTIONARY MUST NOT ENTER THE MAIN BUNDLE. `en.dic` is 540KB. It lives in
 *    `public/dict/` and is fetched on first use; nspell itself is behind a dynamic
 *    `import()` so webpack gives it its own chunk. Nothing here runs — and nothing
 *    is downloaded — until someone actually opens a copy field.
 *
 * 2. A CHECKER THAT CRIES WOLF GETS TURNED OFF. Measured against the real block
 *    fixture, raw en_US flags 17 distinct words in markup that contains no typos at
 *    all: `sectionTitle`, `GetDirections`, `MainImage`, `cmpgn`, `VhPwP`, `ECH`…
 *    Every one is an identifier, an acronym or a URL fragment, so those shapes are
 *    skipped structurally (see `skipToken`) rather than enumerated. And the domain
 *    vocabulary genuinely absent from en_US — measured, not guessed: `homesite`,
 *    `floorplan`, `masterplan`, `townhome`, `pickleball`, `QMI`, `realtor`,
 *    `Lennar` itself — is carried in `SPELL_ALLOW`.
 *
 *    Words that turned out to be IN en_US already are deliberately not carried:
 *    clubhouse, cabana, pergola, lanai, amenities, greenbelt, mudroom, quartz.
 *    Adding them would be dead weight that later reads as load-bearing.
 *
 *    `favourite` and `colour` are NOT allow-listed. They flag, and that is correct
 *    — this is a US homebuilder.
 *
 * 3. COMMUNITY NAMES CANNOT BE A STATIC LIST. Sendero, Edenbrooke, Hardwick,
 *    Greenacres all flag, and the set changes every time a division opens a
 *    community. They are seeded at runtime from the same `/refdata` the pickers
 *    already load (`spellSeedNames`), never hardcoded — a hardcoded list would be
 *    stale within a month and would quietly start flagging real communities.
 *
 * OFFSETS ARE PRESERVED THROUGHOUT. Every issue carries `start`/`end` into the
 * ORIGINAL string, so a caller can highlight or splice without re-finding the word
 * (re-finding by string match would hit the wrong instance of a repeated typo).
 */

/** one flagged word, positioned in the original string */
export type SpellIssue = {
  word: string;
  start: number;
  end: number;
  /** best-first; may be empty — nspell has no suggestion for many non-words */
  suggestions: string[];
};

type Speller = {
  correct(word: string): boolean;
  suggest(word: string): string[];
};

const DICT_BASE = (process.env.PUBLIC_URL || "") + "/dict";
const LS_KEY = "ech.spell.learned.v1";

/* ------------------------------------------------------------------ vocabulary */

/**
 * Domain vocabulary measured to be ABSENT from en_US on 2026-08-22. Anything that
 * en_US already knows is deliberately excluded — see the header note.
 *
 * Inflections are listed explicitly rather than relying on affix flags: these are
 * added through nspell's plain word list, which carries no affix rules, so
 * `homesite` alone would still flag `homesites`.
 */
export const SPELL_ALLOW: string[] = [
  /* the company and its own words */
  "Lennar", "Lennar's", "eblast", "eblasts",
  /* housing stock */
  "homesite", "homesites", "floorplan", "floorplans", "masterplan", "masterplans",
  "townhome", "townhomes", "casita", "casitas", "ramada", "ramadas",
  "QMI", "QMIs", "quickmove",
  /* buying and financing */
  "homebuyer", "homebuyers", "homebuying", "homeownership",
  "prequalify", "prequalified", "prequalification", "preapproval", "preapproved",
  "upsize", "HOA", "HOAs", "realtor", "realtors",
  /* amenities and features */
  "pickleball", "cornhole", "splashpad", "greenspace", "xeriscape", "walkability",
  "walkable", "bonusroom", "flexspace", "ensuite", "backsplash", "undermount",
  "cul-de-sac", "culdesac",
  /* included tech */
  "smarthome", "Wi-Fi", "CoreTech", "Sonos", "Alexa",
  /* internal shorthand that appears in real copy */
  "FTHB", "NSE", "RMT", "ISC", "SFMC", "AMPscript", "CloudPage", "CloudPages",
  /* portals */
  "Zillow", "Trulia", "Redfin",
  /* construction */
  "jobsite", "buildout", "buildable",
];

/* ------------------------------------------------------- protected-region masking */

/**
 * Regions that are not prose and must never be spell-checked. Each is blanked to
 * spaces of EQUAL LENGTH so byte offsets keep pointing at the original string —
 * deleting them instead would shift every subsequent issue.
 *
 * Order matters: AMPscript blocks first (they can contain anything, including
 * angle brackets and quotes), then tokens, then tags, then URLs and emails.
 */
const MASKS: RegExp[] = [
  /%%\[[\s\S]*?\]%%/g,          // AMPscript block
  /%%[^%\n]*%%/g,               // AMPscript inline / merge field
  /\{\{[^}\n]*\}\}/g,           // ECH field token
  /<!--[\s\S]*?-->/g,           // HTML comment (carries the manifest + guardrails)
  /<(style|script)\b[\s\S]*?<\/\1\s*>/gi,  // CSS and JS bodies, whole
  /<[^>]*>/g,                   // HTML tag — [^>] matches newlines ON PURPOSE
  /\b(?:https?:\/\/|www\.)\S+/gi,
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,  // email
  /&(?:[a-z]+|#\d+);/gi,        // HTML entity
  /#[0-9a-f]{3,8}\b/gi,         // hex colour
];

export function maskNonProse(s: string): string {
  let out = String(s ?? "");
  for (const re of MASKS) {
    out = out.replace(re, (m) => " ".repeat(m.length));
  }
  return out;
}

/* ------------------------------------------------------------------- tokenising */

/**
 * A token is a run of letters, digits, apostrophes and inner hyphens. Digits are
 * INCLUDED deliberately: matching letters only would slice `3BR` into `BR` and then
 * flag it, and `2,150` into nothing useful. A token containing a digit is skipped
 * whole in `skipToken`.
 */
const TOKEN_RE = /[A-Za-z0-9][A-Za-z0-9'’]*(?:-[A-Za-z0-9'’]+)*/g;

const VOWEL_RE = /[aeiouyAEIOUY]/;

/** true when this token is not English prose and must not be checked */
export function skipToken(t: string): boolean {
  if (t.length < 3) return true;                    // "an", "of" — nothing to catch
  if (/\d/.test(t)) return true;                    // 3BR, 2150, 55, v2
  if (!VOWEL_RE.test(t)) return true;               // cmpgn, hdr, txt
  if (t === t.toUpperCase() && t.length <= 5) return true;  // CTA, VIP, HOA, EBLST
  /* Any capital past the first character, in a token that is not wholly capitals,
     is an identifier and not an English word: sectionTitle, GetDirections,
     MainImage, VhPwP, VIPImage. `[a-z][A-Z]` was the first attempt and missed
     VIPImage, whose capitals all sit before the lowercase run. */
  if (t !== t.toUpperCase() && /[A-Z]/.test(t.slice(1))) return true;
  if (/_/.test(t)) return true;                     // snake_case
  return false;
}

/** strip a possessive so `Lennar's` is checked as `Lennar` */
function base(t: string): string {
  return t.replace(/['’]s$/i, "").replace(/['’]$/, "");
}

/* --------------------------------------------------------------------- the load */

let sp: Speller | null = null;
let loading: Promise<void> | null = null;
let failed = false;

/** words added this session from live reference data — not persisted */
const seeded = new Set<string>();
/** words Jack explicitly taught it — persisted in localStorage */
let learned = new Set<string>(readLearned());

function readLearned(): string[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeLearned(): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify([...learned]));
  } catch {
    /* private mode / quota — the session still works, it just won't remember */
  }
}

/**
 * Bumped whenever the ANSWER could change: the dictionary finished loading, names
 * were seeded, a word was learned or forgotten. Components re-check on a change,
 * so teaching it a word clears that word from every other field at once instead of
 * only the field it was taught in.
 */
let version = 0;
const listeners = new Set<() => void>();

function bump(): void {
  version++;
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {
      /* one bad subscriber must not stop the rest */
    }
  }
}

export function spellVersion(): number {
  return version;
}

export function spellSubscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function spellReady(): boolean {
  return !!sp;
}

/** true once a load has been attempted and failed — callers hide the UI rather than retry forever */
export function spellFailed(): boolean {
  return failed;
}

/**
 * Builds the speller from already-fetched dictionary bytes and applies all three
 * word lists. Split out from `loadSpell` so tests can drive the REAL dictionary off
 * disk — a mocked speller would prove nothing about the false-positive rate, which
 * is the only thing that decides whether this feature is usable.
 */
export async function spellInit(aff: string, dic: string): Promise<void> {
  const mod = await import("nspell");
  const make = ((mod as unknown as { default?: unknown }).default ?? mod) as (d: {
    aff: string;
    dic: string;
  }) => Speller & { add(w: string): unknown };
  const inst = make({ aff, dic });
  for (const w of SPELL_ALLOW) inst.add(w);
  for (const w of seeded) inst.add(w);
  for (const w of learned) inst.add(w);
  sp = inst;
  bump();
}

/**
 * Fetches the dictionary and builds the speller. Single-flight: concurrent callers
 * share one promise, and a resolved load is a no-op, so every copy field can call
 * this on focus without coordination.
 */
export function loadSpell(): Promise<void> {
  if (sp || failed) return Promise.resolve();
  if (loading) return loading;
  loading = (async () => {
    try {
      const [aff, dic] = await Promise.all([
        fetch(DICT_BASE + "/en.aff").then((r) => {
          if (!r.ok) throw new Error("aff " + r.status);
          return r.text();
        }),
        fetch(DICT_BASE + "/en.dic").then((r) => {
          if (!r.ok) throw new Error("dic " + r.status);
          return r.text();
        }),
      ]);
      await spellInit(aff, dic);
    } catch {
      /* a missing dictionary must never break the Studio — the checker just stays
         silent, which is why every consumer treats [] as "nothing to report" and
         not as "clean" */
      failed = true;
      bump();
    } finally {
      loading = null;
    }
  })();
  return loading;
}

/* ------------------------------------------------------------- the word lists */

/**
 * Feed live reference data — community, masterplan, city and division names — so
 * real place names never flag. Multi-word names are split, because `Garden Square
 * Greenacres` has to clear `Greenacres` on its own.
 *
 * Safe to call repeatedly and before the dictionary loads; anything seeded early is
 * applied when it builds.
 */
export function spellSeedNames(names: Array<string | null | undefined>): void {
  let added = 0;
  for (const n of names) {
    for (const part of String(n ?? "").split(/[^A-Za-z'’-]+/)) {
      if (part.length < 3 || skipToken(part)) continue;
      if (seeded.has(part)) continue;
      seeded.add(part);
      added++;
      if (sp) (sp as unknown as { add(w: string): void }).add(part);
    }
  }
  if (added) bump();
}

/** teach it a word for good */
export function spellLearn(word: string): void {
  const w = base(String(word ?? "").trim());
  if (!w) return;
  learned.add(w);
  writeLearned();
  if (sp) (sp as unknown as { add(w: string): void }).add(w);
  bump();
}

/** unteach a word — takes effect on the next reload, nspell has no remove-after-add */
export function spellForget(word: string): void {
  learned.delete(base(String(word ?? "").trim()));
  writeLearned();
  bump();
}

export function spellLearned(): string[] {
  return [...learned].sort((a, b) => a.localeCompare(b));
}

/** test seam only — resets every piece of module state */
export function __spellReset(): void {
  sp = null;
  loading = null;
  failed = false;
  seeded.clear();
  learned = new Set(readLearned());
  bump();
}

/* ------------------------------------------------------------------ the check */

/**
 * Returns one issue per misspelled word, positioned in the ORIGINAL string.
 *
 * Returns `[]` when the dictionary has not loaded — indistinguishable from "clean",
 * on purpose: a copy field must never render a scary state because a fetch is in
 * flight. Callers that need to know gate on `spellReady()`.
 *
 * A hyphenated compound is checked WHOLE first (`cul-de-sac`, `move-in`) and only
 * then per part, so a legitimate compound that en_US happens not to list does not
 * flag both halves.
 */
export function spellCheckText(text: string, opts?: { limit?: number }): SpellIssue[] {
  const s = String(text ?? "");
  if (!sp || !s) return [];
  /* PIN THE INSTANCE FOR THE WHOLE PASS. `sp` is module-level and nullable — a
     dictionary reload mid-loop would make the closures below unsafe, which is what
     eslint's no-loop-func flags on the compound check. Reading it once also means one
     text never gets checked against two dictionaries. */
  const dict = sp;
  const limit = opts?.limit ?? 50;
  const masked = maskNonProse(s);
  const out: SpellIssue[] = [];

  TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TOKEN_RE.exec(masked))) {
    if (out.length >= limit) break;
    const raw = m[0];
    if (skipToken(raw)) continue;
    const b = base(raw);
    if (!b || skipToken(b)) continue;
    if (dict.correct(b)) continue;

    /* a compound whose whole form is unknown may still be made of known parts */
    if (b.includes("-")) {
      const parts = b.split("-").filter((p) => p.length >= 3 && !skipToken(p));
      if (parts.length && parts.every((p) => dict.correct(p))) continue;
    }
    /* `Homesite` at the start of a sentence: en_US stores lowercase entries, so a
       capitalised first word must also be tried lowercased */
    if (b[0] === b[0].toUpperCase() && dict.correct(b.toLowerCase())) continue;

    out.push({
      word: raw,
      start: m.index,
      end: m.index + raw.length,
      suggestions: dict.suggest(b).slice(0, 5),
    });
  }
  return out;
}

/** convenience for a whole block: field key → issues, empty keys omitted */
export function spellCheckFields(
  fields: Record<string, unknown>,
  keys?: string[],
): Record<string, SpellIssue[]> {
  const out: Record<string, SpellIssue[]> = {};
  for (const k of keys ?? Object.keys(fields)) {
    const v = fields[k];
    if (typeof v !== "string" || !v.trim()) continue;
    const iss = spellCheckText(v);
    if (iss.length) out[k] = iss;
  }
  return out;
}

/** replace one issue's span — offsets make this exact even for a repeated typo */
export function applySuggestion(text: string, iss: SpellIssue, word: string): string {
  const s = String(text ?? "");
  return s.slice(0, iss.start) + word + s.slice(iss.end);
}
